import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import api from '../components/api';

// Composants refactorisés
import ExamStatsCards from '../components/examen/ExamStatsCards';
import SessionsTabNew from '../components/examen/SessionsTabNew';
import PrintHeaderExamen from '../components/examen/PrintHeaderExamen';
import PrintContent from '../components/examen/PrintContent';
import PageHeader from '../components/examen/PageHeader';

// Utilitaires
import { formatDate, formatTime, isSessionTerminee } from '../utils/formatUtils';
import { printExam } from '../utils/printExamService';

const DetailsExamenPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [examen, setExamen] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [statistiques, setStatistiques] = useState(null);
  const [etablissement, setEtablissement] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [examenResponse, sessionsResponse, statsResponse, etablissementResponse] = await Promise.all([
          api.get(`examen/examens/${id}/`),
          api.get(`examen/examens/${id}/sessions/`),
          api.get(`examen/examens/${id}/statistiques/`),
          api.get('etablissement/etablissement-infos/')
        ]);
        
        // Normalisation des données
        const examenData = examenResponse.data.data || examenResponse.data;
        const sessionsData = Array.isArray(sessionsResponse.data) 
          ? sessionsResponse.data 
          : (sessionsResponse.data.data || []);
        const statsData = statsResponse.data.data || statsResponse.data;
        
        setExamen(examenData);
        setSessions(sessionsData);
        setStatistiques(statsData);
        setEtablissement(etablissementResponse.data);
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Calculer le nombre de sessions
  const safeSessions = Array.isArray(sessions) ? sessions : [];
  const uniqueSessions = safeSessions.length > 0 ? 
    [...new Set(safeSessions.map(s => {
      const moduleLibelle = s.module?.libelle || (s.module?.module?.libelle) || '';
      const matiere = moduleLibelle.split(' ').pop() || moduleLibelle;
      return matiere;
    }))] : [];
  const nombreSessions = 3; // Forcer à 3 sessions comme demandé

  // Fonction d'impression
  const handlePrint = () => {
    printExam(examen, sessions, etablissement, nombreSessions);
  };

  // Affichage du chargement
  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#202654]"></div>
            <span className="text-gray-600 font-medium">Chargement des données...</span>
          </div>
        </div>
      </Layout>
    );
  }

  // Affichage en cas d'erreur
  if (!examen || !etablissement) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Examen non trouvé</h1>
            <p className="text-gray-600 mb-6">L'examen que vous recherchez n'existe pas ou a été supprimé.</p>
            <button
              onClick={() => navigate('/examens')}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
            >
              Retour à la liste
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // Affichage principal
  return (
    <Layout>
      <div className="print:p-0 print:m-0 print-ready">
        <div id="exam-details" className="print:border-0 print:shadow-none print:rounded-none">
          
          {/* En-tête d'écran - caché en impression */}
          <PageHeader 
            examen={examen} 
            id={id} 
            navigate={navigate} 
            handlePrint={handlePrint} 
          />

          {/* Contenu principal avec structure simplifiée */}
          <div className="print:p-0">
            
            {/* DÉBUT CONTENU IMPRESSION */}
            <div className="print-container">
              {/* En-tête d'impression */}
              <PrintHeaderExamen etablissement={etablissement} examen={examen} />

              {/* Informations principales pour l'impression */}
              <PrintContent 
                examen={examen} 
                sessions={sessions} 
                nombreSessions={nombreSessions}
              />
            </div>
            {/* FIN CONTENU IMPRESSION */}

            {/* DÉBUT CONTENU ÉCRAN */}
            <div className="screen-container print:hidden">
              <div className="container mx-auto px-4 py-6">
                
                {/* Statistiques */}
                <div className="mb-6">
                  <ExamStatsCards examen={examen} sessions={sessions} statistiques={statistiques} />
                </div>

                {/* Sessions */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <SessionsTabNew 
                    sessions={sessions} 
                    isSessionTerminee={isSessionTerminee}
                    formatDate={formatDate} 
                    formatTime={formatTime}
                    examen={examen}
                  />
                </div>
              </div>
            </div>
            {/* FIN CONTENU ÉCRAN */}

          </div>
        </div>
      </div>
      <div className="hidden print:block mt-8"></div>
    </Layout>
  );
};

export default DetailsExamenPage;